import { Link } from "react-router-dom";
import type { DashboardOverview } from "@/types/dashboard";
import { originText } from "../labels";

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-card border border-background-200 p-5 md:p-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-foreground-900 font-heading">{title}</h3>
        {subtitle && <p className="text-xs text-foreground-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function BarList({ rows, color = "#1B6E4F" }: { rows: Array<{ label: string; count: number }>; color?: string }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-foreground-700 truncate pr-2">{r.label}</span>
            <span className="font-semibold text-foreground-900 tabular-nums">{r.count}</span>
          </div>
          <div className="h-1.5 rounded-full bg-background-100">
            <div className="h-1.5 rounded-full" style={{ width: `${(r.count / max) * 100}%`, backgroundColor: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function TopSpecies({ rows }: { rows: DashboardOverview["topSpecies"] }) {
  return (
    <Card title="Espécies mais frequentes" subtitle="Exemplares cadastrados por espécie">
      {rows.length === 0 ? <p className="text-sm text-foreground-500">Sem dados.</p> : <BarList rows={rows} />}
    </Card>
  );
}

export function RegistrationOrigin({ rows }: { rows: DashboardOverview["registrationOrigin"] }) {
  return (
    <Card title="Origem dos cadastros" subtitle="De onde cada exemplar foi registrado">
      {rows.length === 0 ? (
        <p className="text-sm text-foreground-500">Sem dados.</p>
      ) : (
        <BarList rows={rows.map((r) => ({ label: originText(r.label), count: r.count }))} color="#0052CC" />
      )}
    </Card>
  );
}

export function ProjectsCard({ projects }: { projects: DashboardOverview["projects"] }) {
  return (
    <Card title="Projetos" subtitle="Mais recentes da organização">
      {projects.length === 0 ? (
        <p className="text-sm text-foreground-500">Nenhum projeto cadastrado.</p>
      ) : (
        <ul className="divide-y divide-background-100">
          {projects.map((p) => (
            <li key={p.id}>
              <Link to="/projetos" className="flex items-center gap-3 py-2.5 hover:text-primary-700">
                <i className="ri-folder-chart-line text-lg text-foreground-400" />
                <span className="min-w-0 flex-1 text-sm text-foreground-900 truncate">{p.name}</span>
                <span className="text-xs text-foreground-500 tabular-nums shrink-0">{p.treeCount} exemplares</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
